import { useQueryClient } from "@tanstack/react-query";
import { useState, useCallback, useRef, useEffect } from "react";

import { fetchSubFolderList } from "@/common/services/scenarios";

export interface TreeNode {
  id: number;
  title: string;
  count?: number;
  hasChildren?: boolean;
  children?: TreeNode[];
  isLoaded?: boolean;
  /** users.id of the folder's creator — used to gate delete for the Tester role. */
  createdBy?: number | null;
  /** Project role name of the folder's creator — used to gate delete for the Test Lead role. */
  createdByRoleName?: string | null;
}

interface TreeSnapshot {
  tree: TreeNode[];
  collapsed: Record<number, boolean>;
  activeNodeId: number | null;
}

// Module-level cache so the expanded/loaded folder tree survives navigation away
// from (and back to) the Test Cases page. Keyed by folderType + scriptType +
// versionId so each distinct list keeps its own state. This prevents the tree
// from collapsing + re-selecting root (and re-fetching) every time the user
// returns to the page.
const treeCache = new Map<string, TreeSnapshot>();

export const useFolderTree = (persistKey?: string) => {
  const queryClient = useQueryClient();

  // Restore any cached snapshot for this key on first render.
  const initialSnapshot = persistKey ? treeCache.get(persistKey) : undefined;

  const [folderTree, setFolderTree] = useState<TreeNode[]>(
    initialSnapshot?.tree ?? [],
  );
  const [collapsedMap, setCollapsedMap] = useState<Record<number, boolean>>(
    initialSnapshot?.collapsed ?? {},
  );
  const [loadingNodes, setLoadingNodes] = useState<Set<number>>(new Set());

  // Use ref to avoid stale closure issues
  const folderTreeRef = useRef<TreeNode[]>([]);
  folderTreeRef.current = folderTree;

  // Snapshot captured at mount — used by the consumer to decide whether to skip
  // the rebuild/auto-select of a fresh tree.
  const restoredRef = useRef<TreeSnapshot | undefined>(initialSnapshot);
  const hasPersistedTree =
    !!restoredRef.current && restoredRef.current.tree.length > 0;
  const persistedActiveNodeId = restoredRef.current?.activeNodeId ?? null;

  // Write-through: keep the module cache in sync with the live tree state so a
  // later remount restores the exact same expanded structure.
  useEffect(() => {
    if (!persistKey) return;
    const prev = treeCache.get(persistKey);
    treeCache.set(persistKey, {
      tree: folderTree,
      collapsed: collapsedMap,
      activeNodeId: prev?.activeNodeId ?? persistedActiveNodeId,
    });
  }, [persistKey, folderTree, collapsedMap, persistedActiveNodeId]);

  // Persist the selected folder id without touching the tree snapshot.
  const setPersistedActiveNodeId = useCallback(
    (id: number | null) => {
      if (!persistKey) return;
      const snap = treeCache.get(persistKey);
      if (snap) snap.activeNodeId = id;
      else
        treeCache.set(persistKey, {
          tree: folderTreeRef.current,
          collapsed: {},
          activeNodeId: id,
        });
    },
    [persistKey],
  );

  const initializeTree = useCallback((rootFolders: any[]) => {
    // Dedupe by id — a folder list that momentarily contains the same root twice
    // (e.g. overlapping refetches) must never seed duplicate tree nodes.
    const seen = new Set<number>();
    const formatted: TreeNode[] = [];
    for (const folder of rootFolders) {
      const id = Number(folder.id);
      if (seen.has(id)) continue;
      seen.add(id);
      formatted.push({
        id,
        title: folder.name,
        count: folder.count ?? 0,
        hasChildren: folder.hasChildren ?? false,
        children: [],
        isLoaded: false,
        createdBy: folder.created_by ?? null,
        createdByRoleName: folder.createdByRoleName ?? null,
      });
    }
    setFolderTree(formatted);
    return formatted;
  }, []);

  const fetchAndAddSubFolders = useCallback(
    async (parentId: number) => {
      try {
        setLoadingNodes((prev) => new Set(prev).add(parentId));
        const { data: subfolders } = await queryClient.fetchQuery({
          queryKey: ["subFolderList", parentId],
          queryFn: () => fetchSubFolderList(parentId),
          staleTime: 5 * 60 * 1000,
        });

        const updatedChildren: TreeNode[] = subfolders.map((folder: any) => ({
          id: Number(folder.id),
          title: folder.name,
          count: folder.count ?? 0,
          hasChildren: folder.hasChildren ?? false,
          children: [],
          isLoaded: false,
          createdBy: folder.created_by ?? null,
          createdByRoleName: folder.createdByRoleName ?? null,
        }));

        const updateTree = (nodes: TreeNode[]): TreeNode[] =>
          nodes.map((node) => {
            if (node.id === parentId) {
              return {
                ...node,
                children: updatedChildren,
                isLoaded: true,
              };
            }
            return node.children
              ? { ...node, children: updateTree(node.children) }
              : node;
          });

        setFolderTree((prev) => updateTree(prev));

        // NOTE: no eager prefetch of descendant case lists here. Expanding a folder
        // must only fetch its own subfolder list — case pages load for the
        // selected folder (FolderCases) or via the debounced hover prefetch
        // (usePrefetchFolder). A recursive prefetch here caused an N+1 storm
        // (one /test_cases/filter call per descendant folder on every expand).
      } catch (error) {
        console.error("Failed to fetch subfolders", error);
      } finally {
        setLoadingNodes((prev) => {
          const next = new Set(prev);
          next.delete(parentId);
          return next;
        });
      }
    },
    [queryClient],
  );

  /**
   * Expand a folder AND every descendant folder in one action. Recursively
   * fetches each folder's subfolder list (cached via React Query, so re-expands
   * are instant), builds the full subtree, and marks every folder with children
   * as expanded. This only fetches the folder STRUCTURE (lightweight
   * subFolderList calls) — never per-folder case lists — so it can't trigger the
   * N+1 case-list storm. Used so a single click reveals the whole subtree
   * instead of forcing the user to expand level by level.
   */
  const expandAllDescendants = useCallback(
    async (rootNodeId: number) => {
      // Optimistically expand the clicked node immediately for a snappy UI
      setCollapsedMap((prev) => ({ ...prev, [rootNodeId]: false }));

      const processNode = async (nodeId: number) => {
        setLoadingNodes((prev) => new Set(prev).add(nodeId));
        let subfolders: any[] = [];
        try {
          const res = await queryClient.fetchQuery({
            queryKey: ["subFolderList", nodeId],
            queryFn: () => fetchSubFolderList(nodeId),
            staleTime: 5 * 60 * 1000,
          });
          subfolders = res?.data ?? [];
        } catch (error) {
          console.error("Failed to fetch subfolders", error);
        } finally {
          setLoadingNodes((prev) => {
            const next = new Set(prev);
            next.delete(nodeId);
            return next;
          });
        }

        const children: TreeNode[] = subfolders.map((f: any) => ({
          id: Number(f.id),
          title: f.name,
          count: f.count ?? 0,
          hasChildren: f.hasChildren ?? false,
          children: [],
          isLoaded: false,
          createdBy: f.created_by ?? null,
          createdByRoleName: f.createdByRoleName ?? null,
        }));

        // Immediately attach these children to the tree so UI updates incrementally!
        setFolderTree((prev) => {
          const attach = (nodes: TreeNode[]): TreeNode[] =>
            nodes.map((n) => {
              if (n.id === nodeId) {
                // If it already had children, preserve them, but usually it's empty
                return { ...n, children, isLoaded: true };
              }
              return n.children ? { ...n, children: attach(n.children) } : n;
            });
          return attach(prev);
        });

        // Fetch and attach children to tree (so UI is snappy on next click)
        // Note: we NO LONGER force them to be visually expanded here, per user request.

        // Recursively process children that actually have subfolders
        const branchChildren = children.filter((c) => c.hasChildren);
        await Promise.all(branchChildren.map((c) => processNode(c.id)));
      };

      await processNode(rootNodeId);
    },
    [queryClient],
  );

  /**
   * Expand a specific PATH of folder ids (root → … → leaf) and leave every node
   * on that path open. Unlike expandAllDescendants this only loads + opens the
   * ancestors directly on the path (plus the leaf), not every sibling subtree —
   * used to restore a deep-linked / refreshed folder (?folder=<id>) by revealing
   * just the chain down to it. Each level is fetched from the cached
   * subFolderList query, so it's cheap and never touches per-folder case lists.
   * Returns once the chain is attached so callers can then select the leaf.
   */
  const expandPath = useCallback(
    async (chain: number[]) => {
      if (!chain || chain.length === 0) return;

      const toNodes = (subfolders: any[]): TreeNode[] =>
        (subfolders ?? []).map((f: any) => ({
          id: Number(f.id),
          title: f.name,
          count: f.count ?? 0,
          hasChildren: f.hasChildren ?? false,
          children: [],
          isLoaded: false,
          createdBy: f.created_by ?? null,
          createdByRoleName: f.createdByRoleName ?? null,
        }));

      const attachChildren = (
        nodes: TreeNode[],
        parentId: number,
        children: TreeNode[],
      ): TreeNode[] =>
        nodes.map((node) => {
          if (node.id === parentId) {
            // Preserve any already-loaded children (and their expanded subtrees);
            // only fill in entries that aren't present yet so we never collapse
            // a branch the user already opened.
            const existing = node.children ?? [];
            if (node.isLoaded && existing.length > 0) {
              const have = new Set(existing.map((c) => c.id));
              const additions = children.filter((c) => !have.has(c.id));
              return additions.length > 0
                ? {
                    ...node,
                    children: [...existing, ...additions],
                    isLoaded: true,
                  }
                : { ...node, isLoaded: true };
            }
            return { ...node, children, isLoaded: true };
          }
          return node.children
            ? {
                ...node,
                children: attachChildren(node.children, parentId, children),
              }
            : node;
        });

      // Load + attach children for every folder on the path (so the next level
      // down exists in the tree). The leaf is loaded too so its contents show.
      for (const id of chain) {
        setLoadingNodes((prev) => new Set(prev).add(id));
        try {
          const res = await queryClient.fetchQuery({
            queryKey: ["subFolderList", id],
            queryFn: () => fetchSubFolderList(id),
            staleTime: 5 * 60 * 1000,
          });
          const children = toNodes(res?.data ?? []);
          setFolderTree((prev) => attachChildren(prev, id, children));
        } catch (error) {
          console.error("Failed to expand folder path", error);
        } finally {
          setLoadingNodes((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      }

      setCollapsedMap((prev) => {
        const next = { ...prev };
        for (const id of chain) next[id] = false;
        return next;
      });
    },
    [queryClient],
  );

  const toggleCollapse = useCallback(
    (nodeId: number, node: TreeNode) => {
      const isCollapsed = collapsedMap[nodeId] ?? true;
      const willOpen = isCollapsed;

      setCollapsedMap((prev) => ({ ...prev, [nodeId]: !isCollapsed }));

      if (willOpen && !node.isLoaded) {
        fetchAndAddSubFolders(nodeId);
      }
    },
    [collapsedMap, fetchAndAddSubFolders],
  );

  const removeFolder = useCallback(
    (folderId: number) => {
      const removeFromTree = (nodes: TreeNode[]): TreeNode[] =>
        nodes
          .filter((node) => node.id !== folderId)
          .map((node) =>
            node.children
              ? { ...node, children: removeFromTree(node.children) }
              : node,
          );

      setFolderTree((prev) => removeFromTree(prev));

      // Also prune the folder from every cached subFolderList so a later
      // background folder-list refetch (which re-runs the reconcile effect and
      // feeds these caches back through syncSubFolders) can't re-append the
      // folder we just removed. subFolderList queries are populated via
      // fetchQuery and have no active observer, so invalidation alone never
      // refetches them — without this the deleted (sub)folder reappears in the
      // tree until a manual refresh.
      const subCaches = queryClient.getQueriesData<any>({
        queryKey: ["subFolderList"],
      });
      for (const [key, cached] of subCaches) {
        const list = (cached as any)?.data;
        if (!Array.isArray(list)) continue;
        const filtered = list.filter(
          (f: any) => Number(f?.id) !== Number(folderId),
        );
        if (filtered.length !== list.length) {
          queryClient.setQueryData(key, { ...(cached as any), data: filtered });
        }
      }
    },
    [queryClient],
  );

  const isNodeCollapsed = useCallback(
    (nodeId: number) => {
      return collapsedMap[nodeId] ?? true;
    },
    [collapsedMap],
  );

  const isNodeLoading = useCallback(
    (nodeId: number) => {
      return loadingNodes.has(nodeId);
    },
    [loadingNodes],
  );

  const addRootFolder = useCallback(
    (folder: {
      id: number;
      name: string;
      count?: number;
      hasChildren?: boolean;
      created_by?: number | null;
    }) => {
      const id = Number(folder.id);
      const newNode: TreeNode = {
        id,
        title: folder.name,
        count: folder.count ?? 0,
        hasChildren: folder.hasChildren ?? false,
        children: [],
        isLoaded: false,
        createdBy: folder.created_by ?? null,
      };
      // Guard against re-adding an existing root (idempotent) — prevents the
      // duplicate-folder glitch when this races a refetch/init.
      setFolderTree((prev) =>
        prev.some((n) => n.id === id) ? prev : [...prev, newNode],
      );
    },
    [],
  );

  const addSubFolder = useCallback(
    (
      parentId: number,
      folder: {
        id: number;
        name: string;
        count?: number;
        hasChildren?: boolean;
        created_by?: number | null;
      },
    ) => {
      const newNode: TreeNode = {
        id: Number(folder.id),
        title: folder.name,
        count: folder.count ?? 0,
        hasChildren: folder.hasChildren ?? false,
        children: [],
        isLoaded: false,
        createdBy: folder.created_by ?? null,
      };
      const addToTree = (nodes: TreeNode[]): TreeNode[] =>
        nodes.map((node) => {
          if (node.id === parentId) {
            // Idempotent — never append a child that's already present.
            if ((node.children ?? []).some((c) => c.id === newNode.id)) {
              return { ...node, isLoaded: true };
            }
            return {
              ...node,
              children: [...(node.children || []), newNode],
              isLoaded: true,
            };
          }
          return node.children
            ? { ...node, children: addToTree(node.children) }
            : node;
        });
      setFolderTree((prev) => addToTree(prev));
    },
    [],
  );

  const renameFolder = useCallback((folderId: number, newName: string) => {
    const renameInTree = (nodes: TreeNode[]): TreeNode[] =>
      nodes.map((node) => {
        if (node.id === folderId) {
          return { ...node, title: newName };
        }
        return node.children
          ? { ...node, children: renameInTree(node.children) }
          : node;
      });
    setFolderTree((prev) => renameInTree(prev));
  }, []);

  /**
   * Merge fresh server metadata (count / hasChildren / title) into the existing
   * tree IN PLACE, keyed by folder id. Unlike initializeTree this preserves each
   * node's loaded `children` and `isLoaded` flag, so refreshing case-count
   * badges after a test-case create/delete never collapses or drops expanded
   * subtrees. Nodes absent from the map keep their current values.
   */
  const reconcileCounts = useCallback(
    (
      meta: Map<
        number,
        { count?: number; hasChildren?: boolean; title?: string }
      >,
    ) => {
      if (meta.size === 0) return;
      let changed = false;
      const apply = (nodes: TreeNode[]): TreeNode[] =>
        nodes.map((node) => {
          const m = meta.get(node.id);
          const nextChildren = node.children
            ? apply(node.children)
            : node.children;
          if (m) {
            const nextCount = m.count ?? node.count;
            const nextHasChildren = m.hasChildren ?? node.hasChildren;
            const nextTitle = m.title ?? node.title;
            if (
              nextCount !== node.count ||
              nextHasChildren !== node.hasChildren ||
              nextTitle !== node.title ||
              nextChildren !== node.children
            ) {
              changed = true;
              return {
                ...node,
                count: nextCount,
                hasChildren: nextHasChildren,
                title: nextTitle,
                children: nextChildren,
              };
            }
            return node;
          }
          if (nextChildren !== node.children) {
            changed = true;
            return { ...node, children: nextChildren };
          }
          return node;
        });
      setFolderTree((prev) => {
        const next = apply(prev);
        return changed ? next : prev;
      });
    },
    [],
  );

  /**
   * Merge freshly-fetched root folders into the existing tree: append any root
   * folder that isn't already present (e.g. created by the AI generator or in
   * another tab) WITHOUT rebuilding the tree, so expanded/loaded subtrees and
   * the current selection are preserved. Existing nodes are left untouched here
   * (their counts/titles are refreshed separately by reconcileCounts).
   */
  const syncRootFolders = useCallback((rootFolders: any[]) => {
    setFolderTree((prev) => {
      const existingIds = new Set(prev.map((n) => n.id));
      const additions: TreeNode[] = [];
      for (const folder of rootFolders ?? []) {
        if (folder?.id == null) continue;
        if (folder.parent_id != null) continue;
        const id = Number(folder.id);
        if (existingIds.has(id)) continue;
        existingIds.add(id);
        additions.push({
          id,
          title: folder.name,
          count: folder.count ?? 0,
          hasChildren: folder.hasChildren ?? false,
          children: [],
          isLoaded: false,
          createdBy: folder.created_by ?? null,
          createdByRoleName: folder.createdByRoleName ?? null,
        });
      }
      return additions.length > 0 ? [...prev, ...additions] : prev;
    });
  }, []);

  /**
   * Append newly-appeared subfolders under an already-loaded parent without
   * collapsing or rebuilding it. Used so nested folders created by the AI
   * generator under an expanded parent show up without a page reload.
   */
  const syncSubFolders = useCallback((parentId: number, subFolders: any[]) => {
    setFolderTree((prev) => {
      let changed = false;
      const apply = (nodes: TreeNode[]): TreeNode[] =>
        nodes.map((node) => {
          if (node.id === parentId) {
            // Only merge once the parent's children have been loaded — an
            // unloaded parent will fetch the full fresh list on expand.
            if (!node.isLoaded) return node;
            const existing = new Set((node.children ?? []).map((c) => c.id));
            const additions: TreeNode[] = [];
            for (const f of subFolders ?? []) {
              if (f?.id == null) continue;
              const id = Number(f.id);
              if (existing.has(id)) continue;
              existing.add(id);
              additions.push({
                id,
                title: f.name,
                count: f.count ?? 0,
                hasChildren: f.hasChildren ?? false,
                children: [],
                isLoaded: false,
                createdBy: f.created_by ?? null,
                createdByRoleName: f.createdByRoleName ?? null,
              });
            }
            if (additions.length === 0) return node;
            changed = true;
            return {
              ...node,
              children: [...(node.children ?? []), ...additions],
            };
          }
          return node.children
            ? { ...node, children: apply(node.children) }
            : node;
        });
      const next = apply(prev);
      return changed ? next : prev;
    });
  }, []);

  const collapseAll = useCallback(() => {
    setCollapsedMap({});
  }, []);

  return {
    folderTree,
    collapsedMap,
    initializeTree,
    fetchAndAddSubFolders,
    expandAllDescendants,
    expandPath,
    toggleCollapse,
    collapseAll,
    removeFolder,
    isNodeCollapsed,
    isNodeLoading,
    addRootFolder,
    addSubFolder,
    renameFolder,
    reconcileCounts,
    syncRootFolders,
    syncSubFolders,
    setFolderTree,
    hasPersistedTree,
    persistedActiveNodeId,
    setPersistedActiveNodeId,
  };
};
